import React, { useMemo } from 'react';
import { AdminRole, GameProvider } from '../../../types';
import {
  ROLE_PERMISSIONS,
  getPermissionLabel,
  roleUsesGameProviders,
} from '../../../config/permissions';

interface RolePermissionSummaryProps {
  role: AdminRole;
  /** Provider codes this admin is scoped to (for game-manager roles). Read-only display. */
  managedGameProviders?: string[];
  /** Full provider list to resolve codes → names. */
  gameProviders?: GameProvider[];
  /** Show the managed game providers block for game-manager roles. Default true. */
  showGameProviders?: boolean;
  className?: string;
}

/**
 * Read-only summary of the permissions a role grants, shown with human-readable names
 * (not raw variable codes). Reused across Create Admin, the Permissions view popup, and the
 * Manage Admin modal so all three present role permissions identically. system_admin and
 * super_admin have full access, so their permission list is not enumerated.
 */
const RolePermissionSummary: React.FC<RolePermissionSummaryProps> = ({
  role,
  managedGameProviders = [],
  gameProviders = [],
  showGameProviders = true,
  className = '',
}) => {
  const fullAccess = role === 'system_admin' || role === 'super_admin';

  const permissionLabels = useMemo(
    () =>
      (ROLE_PERMISSIONS[role] ?? [])
        .filter((p) => p !== 'dashboard')
        .map((p) => ({ code: p, label: getPermissionLabel(p) })),
    [role]
  );

  const providerNames = useMemo(() => {
    if (!managedGameProviders.length) return [];
    return managedGameProviders.map((code) => {
      const match = gameProviders.find((gp) => gp.code === code);
      return match ? match.name : code;
    });
  }, [managedGameProviders, gameProviders]);

  const showProviders = showGameProviders && roleUsesGameProviders(role);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="text-sm font-medium">Permissions granted by role</label>
      <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50">
        {fullAccess ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
            Full access — all permissions
          </span>
        ) : permissionLabels.length === 0 ? (
          <span className="text-xs text-muted-foreground">No permissions for this role.</span>
        ) : (
          permissionLabels.map((p) => (
            <span
              key={p.code}
              className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium"
            >
              {p.label}
            </span>
          ))
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Permissions are determined by the selected role and applied automatically.
      </p>

      {showProviders && (
        <div className="space-y-2 pt-2">
          <label className="text-sm font-medium">Managed Game Providers</label>
          <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50">
            {providerNames.length === 0 ? (
              <span className="text-xs text-muted-foreground">
                No specific game providers assigned.
              </span>
            ) : (
              providerNames.map((name, idx) => (
                <span
                  key={`${name}-${idx}`}
                  className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium"
                >
                  {name}
                </span>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermissionSummary;
